import {
  type BucketGranularity,
  deriveBucketEnd,
  formatAvailabilityDate,
} from "~/shared/kpi"

const MS_PER_DAY = 86_400_000

/** Local calendar date as 'YYYY-MM-DD' (matches the KPI window builders). */
function localDateOnly(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
}

/** Date-only string shifted by whole days (UTC arithmetic — no DST drift). */
function shiftDays(dateOnly: string, days: number): string {
  return new Date(Date.parse(`${dateOnly}T00:00:00Z`) + days * MS_PER_DAY)
    .toISOString()
    .slice(0, 10)
}

/**
 * Column-header note for a bucket that is still in progress (its derived end
 * is today or later), or null for a closed bucket.
 *
 * The daily window ends yesterday, so daily buckets never qualify. The weekly
 * and monthly windows run through today, so their newest bucket is usually
 * partial — and the YoY merge still pairs it with the FULL prior-year
 * week/month (the comparison window deliberately extends through the end of
 * that period, see buildKpiWindows). Nothing else in the table says so; this
 * note does, naming both ranges.
 */
export function partialBucketNote(
  bucketStart: string,
  comparisonStart: string,
  granularity: BucketGranularity,
  today: string = localDateOnly(new Date()),
): string | null {
  const start = bucketStart.slice(0, 10)
  if (deriveBucketEnd(start, granularity) < today) return null

  const yesterday = shiftDays(today, -1)
  const soFar =
    yesterday < start
      ? "no complete days yet"
      : `${formatAvailabilityDate(start)} – ${formatAvailabilityDate(yesterday)} so far`
  const compStart = comparisonStart.slice(0, 10)
  const compEnd = deriveBucketEnd(compStart, granularity)
  return `${granularity === "month" ? "Month" : "Week"} in progress: ${soFar}. YoY compares against the full ${granularity} ${formatAvailabilityDate(compStart)} – ${formatAvailabilityDate(compEnd)}.`
}
